import { api } from "@/lib/api";
import {
  mapWorkerFromDto,
  mapWorkerToDto,
  Worker,
  WorkerDtoSchema,
} from "@/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import {
  getPersonalInfoQueryOptions,
  getWorkerQueryOptions,
  getWorkersQueryOptions,
} from "./get.hooks";

export function useUpdateWorker(opts?: {
  onSuccess?: (worker: Worker) => void;
  onError?: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (worker: Worker) => {
      const dto = mapWorkerToDto(worker);
      const result = await api.put("/api/workers", dto, WorkerDtoSchema);
      return mapWorkerFromDto(result);
    },
    onSuccess: (worker) => {
      queryClient.setQueryData(getWorkersQueryOptions.queryKey, (old) =>
        old?.map((w) => (w.id === worker.id ? worker : w)),
      );
      queryClient.setQueryData(
        getWorkerQueryOptions(worker.id).queryKey,
        () => worker,
      );
      queryClient.invalidateQueries({
        queryKey: getPersonalInfoQueryOptions.queryKey,
      });
      toast({
        title: "toast.workerUpdated",
        variant: "destructive",
      });
      opts?.onSuccess?.(worker);
    },
    onError: () => {
      toast({
        title: "toast.updateWorkerError",
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
